<template>
	<div
		ref="action_menu_item"
		:class="{ 'on-focus': isFocused }"
		@mouseenter="setCardVisibility(true)"
		@mouseleave="setCardVisibility(false)"
		class="action-container display-flex flex-row"
	>
		<div class="width-100 display-flex flex-row" @click="updateNodeAction">
			<div class="icon-card flex-centerize">
				<AppIcon
					:width="16"
					:name="actionIcon"
					:class="{ 'resized-icon': isActionOriginalAppServiceTypeIsPly }"
					v-if="((action && action.service_slug) || (actionApp && actionApp.service_slug)) && actionIcon"
				/>
			</div>
			<div class="action-details-container flex-space-between--center">
				<div class="flex-centerize flex-1">
					<div class="title-section flex-1">
						<div v-if="showAppNamePreview && action.service_name" class="app-name">
							{{ action.service_name }}
						</div>
						<div class="action-title">
							{{ action.title }}
						</div>
					</div>
				</div>
				<Row class="flex-align-center">
					<div class="mr-2 ml-1" v-if="actionInfo?.description">
						<AppTooltip noArrow :text="actionInfo.description">
							<AppIcon :name="'action-info'" />
						</AppTooltip>
					</div>
					<AppIcon v-if="action.is_multiple" :name="'action-table-data'" class="mr-2" />
					<v-icon small class="mr-2" v-if="action.browserAction">mdi-puzzle</v-icon>
					<ActionCreditsRequiredMenu :action="action" v-if="showCreditsIcon" class="mr-2" />
					<ActionUpgradeRequiredMenu :workflow="editWorkflow" :action="action" v-if="showUpgradeRequiredIcon" class="mr-2" />
				</Row>
			</div>
		</div>
		<div v-if="showInfoCard" class="info-card-container" :style="`top: ${containerFixedY - 110}px`">
			<ActionInfoCard :title="action.title" :data="actionInfo" />
		</div>
	</div>
</template>

<script>
import { ServiceType } from '@ply-io/ply-front-shared-utilities';

import ActionInfoCard from './ActionInfoCard';
import ActionCreditsRequiredMenu from '@/components/BlockEditor/IntegrationEditor/ActionCreditsRequiredMenu.vue';
import ActionUpgradeRequiredMenu from '@/components/BlockEditor/IntegrationEditor/ActionUpgradeRequiredMenu.vue';
import { getService, getServiceActionCollection } from '@/store/modules/apps/getters';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters.js';
import { mapGettersShorthand } from '@/store/utils/mappers';
import AppActionService from '@/utils/AppActionService';
import AppService from '@/utils/AppService';
const HOVER_DELAY_TIME = 300; //milliseconds till info card will show and hide

export default {
	name: 'ActionMenuItem',
	components: { ActionInfoCard, ActionUpgradeRequiredMenu, ActionCreditsRequiredMenu },
	props: {
		action: { type: Object },
		appIconName: { type: String },
		focusedItemUUID: { type: String },
		showAppNamePreview: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ getService, getServiceActionCollection, editWorkflow }),
		actionInfo() {
			return this.action.info;
		},
		showUpgradeRequiredIcon() {
			return AppActionService.isActionRestricted(this.action) || AppActionService.isActionOfSubFlow(this.action);
		},
		showCreditsIcon() {
			return AppActionService.isActionCreditBased(this.action);
		},
		hasInfoData() {
			return this.actionInfo && Object.keys(this.actionInfo).length;
		},
		isFocused() {
			const uuid = AppService.getUniqueId('action', this.action.id, this.action.service_name);
			return this.focusedItemUUID === uuid;
		},
		actionOriginalApp() {
			return this.getService(this.action.service_id);
		},
		isActionOriginalAppServiceTypeIsPly() {
			return this.actionOriginalApp?.type === ServiceType.Ply;
		},
		actionApp() {
			// in case the action is part of service action collection, this computed returns the ServiceActionCollection
			// actionOriginalApp returns the regular Service
			return AppActionService.doesActionHasServiceActionCollection(this.action)
				? this.getServiceActionCollection(AppActionService.getActionServiceActionCollection(this.action))
				: this.actionOriginalApp;
		},
		actionIcon() {
			return AppActionService.getActionIcon({
				action: this.action,
				app: this.actionApp,
				originalApp: this.actionOriginalApp,
			});
		},
	},
	methods: {
		updateNodeAction() {
			this.$emit('action-selected', this.action);
		},
		setCardVisibility(newState) {
			if (this.hasInfoData) {
				setTimeout(() => {
					if (!this.$refs.action_menu_item) {
						return;
					}
					const boundingClientRect = this.$refs.action_menu_item.getBoundingClientRect();
					this.containerFixedY = boundingClientRect.y;
					this.showInfoCard = newState;
				}, HOVER_DELAY_TIME);
			}
		},
		onKeyDown(event) {
			if (this.isFocused) {
				if (event.keyCode === 13) {
					this.updateNodeAction();
				}
			}
		},
		removeOnKeyDownListener() {
			if (this.onKeyDownHandler) {
				window.removeEventListener('keydown', this.onKeyDownHandler, true);
				this.onKeyDownHandler = null;
			}
		},
		listenOnKeyDown() {
			this.removeOnKeyDownListener();
			this.onKeyDownHandler = (e) => this.onKeyDown(e);
			window.addEventListener('keydown', this.onKeyDownHandler, true);
		},
	},
	mounted() {
		this.listenOnKeyDown();
	},
	watch: {
		isFocused: {
			handler(newV) {
				this.setCardVisibility(newV);
				this.$refs.action_menu_item && newV && this.$emit('scroll-to-element', this.$refs.action_menu_item.getBoundingClientRect().y - 400);
			},
		},
	},
	data: () => ({
		HOVER_DELAY_TIME,
		containerFixedY: 0,
		showInfoCard: false,
		onKeyDownHandler: null,
	}),
	beforeDestroy() {
		this.removeOnKeyDownListener();
	},
};
</script>

<style scoped lang="scss">
$side-padding: 23px;

.on-focus {
	background-color: var(--app-selection-hover-background);
	.app-name {
		opacity: 1 !important;
	}
}

.action-container {
	width: 100%;
	height: 38px;
	cursor: pointer;
	margin-top: 15px;
	padding: 0px $side-padding;

	&:hover {
		background-color: var(--app-selection-hover-background);
		.app-name {
			opacity: 1 !important;
			transition: 0.3s;
		}
		.app-name + .action-title {
			margin-top: 7px;
			transition: 0.3s;
		}
	}

	.icon-card {
		width: 26px;
		height: 26px;
		border-radius: 7px;
		background-color: var(--bg-color);
		border: 1px solid var(--border-color);

		.resized-icon {
			transform: scale(0.6);
		}
	}

	.action-details-container {
		width: 100%;
		padding: 0 0 0 10px;

		.title-section {
			position: relative;

			.app-name {
				position: absolute;
				top: 0;
				left: 0;
				margin-top: 0px;
				font-size: 10px;
				line-height: 4px;
				color: var(--description-text);
				opacity: 0;
				transition: 0.3s;
			}
		}
		.action-title {
			font-size: 14px;
			color: var(--bold-title);
		}
		.info-icon {
			margin: 2px 0px 0px 5px;
		}
	}

	.info-card-container {
		$width: 386px;

		width: $width;
		left: -$width;
		cursor: default;
		position: fixed;
	}
}
</style>
